'use strict'

// Pure classification + descriptor helpers for the long-cycle drag-to-day
// landing dialog. Kept dependency-free so they're trivially unit-testable.
//
// Authoritative ranked anchor options come from a server endpoint
// (see server/src/hmi/routeOptimizer/cadencePlacementService.js). This module
// only handles client-side stop classification, landing-date descriptor
// derivation, and label formatting for whatever the server returns.

// Client-local mirror of the server's long-cycle frequency-code set.
// Source of truth: server/src/hmi/routeOptimizer/cadencePlacementService.js
// (CADENCE_WINDOW_DAYS / LONG_CYCLE_FREQ_CODES). Keep in sync manually.
export const LONG_CYCLE_FREQ_CODES = ['M', 'M1', 'M2', 'M3', 'M4', 'Q', 'SA', 'A']

const LONG_CYCLE_FREQ_CODE_SET = new Set(LONG_CYCLE_FREQ_CODES)

export const isLongCycleStop = (stop) => {
  const code = String(stop && stop.frequencyCode || '').trim().toUpperCase()
  return LONG_CYCLE_FREQ_CODE_SET.has(code)
}

const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const MONTH_SHORT_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// 'YYYY-MM-DD' -> 'Aug 3'. String math only — no Date construction, so there
// is no local-timezone drift on the UTC ymd the server returns. Invalid input
// echoes back unchanged (display context — never throws).
export const formatShortDate = (ymd) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || '').trim())
  if (!match) return String(ymd || '')
  const monthName = MONTH_SHORT_NAMES[Number(match[2]) - 1]
  return monthName ? `${monthName} ${Number(match[3])}` : String(ymd)
}

const parseYmdToUTC = (ymd) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || '').trim())
  if (!match) return null
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const ms = Date.UTC(year, month - 1, day)
  if (Number.isNaN(ms)) return null
  // Guard against JS Date normalizing an invalid calendar date (e.g. 2026-02-30).
  const check = new Date(ms)
  if (check.getUTCFullYear() !== year || check.getUTCMonth() !== month - 1 || check.getUTCDate() !== day) return null
  return ms
}

const DAY_MS = 24 * 60 * 60 * 1000

// Resolve which fortnight bucket ('A' or 'B') a landing date falls in, given
// the current fortnight's two week-start dates. Dates inside [A, A+7) are
// 'A'; inside [B, B+7) are 'B'. Dates outside that window are extended via
// 14-day arithmetic from the A start: weeks-since-A-start even -> 'A', odd -> 'B'.
const resolveWeekType = (landingMs, weekStartDates) => {
  if (!weekStartDates || typeof weekStartDates !== 'object') return null
  const aMs = parseYmdToUTC(weekStartDates.A)
  // bMs is parsed for shape validation only — the parity math below runs
  // entirely off the A start, by design.
  const bMs = parseYmdToUTC(weekStartDates.B)
  if (aMs === null || bMs === null) return null

  const diffDays = Math.floor((landingMs - aMs) / DAY_MS)
  const weeks = Math.floor(diffDays / 7)
  // Normalize parity to [0, 1) even for negative week counts.
  const parity = ((weeks % 2) + 2) % 2
  return parity === 0 ? 'A' : 'B'
}

export const deriveLandingDescriptors = (landingYmd, weekStartDates) => {
  const landingMs = parseYmdToUTC(landingYmd)
  if (landingMs === null) {
    return { nth: null, weekday: null, weekdayName: null, weekType: null, isLastOfMonth: null }
  }

  const date = new Date(landingMs)
  const year = date.getUTCFullYear()
  const month = date.getUTCMonth()
  const day = date.getUTCDate()
  const weekday = date.getUTCDay()

  const nth = Math.floor((day - 1) / 7) + 1

  // Last occurrence of this weekday in the month if adding 7 days rolls into
  // the next month.
  const nextOccurrenceMs = landingMs + 7 * DAY_MS
  const nextDate = new Date(nextOccurrenceMs)
  const isLastOfMonth = nextDate.getUTCMonth() !== month || nextDate.getUTCFullYear() !== year

  const weekType = resolveWeekType(landingMs, weekStartDates)

  return {
    nth,
    weekday,
    weekdayName: WEEKDAY_NAMES[weekday],
    weekType,
    isLastOfMonth
  }
}

// 1st, 2nd, 3rd, 4th, ... 11th, 12th, 13th, 21st, ... — handles the
// teens exception (11-13 always "th" regardless of trailing digit).
export const ordinal = (n) => {
  const value = Number(n)
  const remainder100 = value % 100
  if (remainder100 >= 11 && remainder100 <= 13) return `${value}th`
  const remainder10 = value % 10
  if (remainder10 === 1) return `${value}st`
  if (remainder10 === 2) return `${value}nd`
  if (remainder10 === 3) return `${value}rd`
  return `${value}th`
}

const formatIntervalBaseDate = (baseDateYmd) => {
  const ms = parseYmdToUTC(baseDateYmd)
  if (ms === null) return ''
  const date = new Date(ms)
  return `${MONTH_SHORT_NAMES[date.getUTCMonth()]} ${date.getUTCDate()}`
}

// Server-side anchor params carry a NUMERIC weekday (0..6, see
// longCycle.controller validateAnchorParams); weekdayName only exists on
// client-derived descriptors. Accept either so persisted anchors and
// server-built options both label correctly.
const resolveWeekdayName = (params) =>
  params.weekdayName || WEEKDAY_NAMES[params.weekday] || null

export const formatAnchorLabel = (option) => {
  if (!option || typeof option !== 'object') return ''
  const { anchorType, companionName, routeName } = option
  // A `params = {}` destructure default only covers undefined, not null —
  // normalize explicitly so a null params from the server can never throw.
  const params = option.params || {}

  switch (anchorType) {
    case 'nth_weekday': {
      const { nth, isLast } = params
      const weekdayName = resolveWeekdayName(params)
      if (!weekdayName) return ''
      if (isLast || Number(nth) >= 5) return `Last ${weekdayName}`
      return `${ordinal(nth)} ${weekdayName}`
    }

    case 'ab_weekday': {
      const { weekType } = params
      const weekdayName = resolveWeekdayName(params)
      if (!weekType || !weekdayName) return ''
      return `${weekType}-week ${weekdayName}`
    }

    case 'interval': {
      const { intervalMonths, baseDate } = params
      const formattedDate = formatIntervalBaseDate(baseDate)
      if (!intervalMonths || !formattedDate) return ''
      return `Every ${intervalMonths} months from ${formattedDate}`
    }

    case 'companion': {
      const parts = []
      const weekdayName = resolveWeekdayName(params)
      if (weekdayName) parts.push(weekdayName)
      if (routeName) parts.push(routeName)
      const suffix = parts.length ? ` (${parts.join(' · ')})` : ''
      return `With ${companionName || 'companion'}${suffix}`
    }

    default:
      return ''
  }
}
